import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import styles from "../AdmissionApplicationForm.module.css";
import AxiosInstance, {
  admissionLogo1,
  admissionLogo2,
} from "../../../services/Api";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaIdCard } from "react-icons/fa";
import {
  CustomButton,
  CustomDatePicker,
  CustomTextInput,
} from "../../../components";
import PopupModal from "../../../components/PopupModal/PopupModal";

const ApplicationXIIWithdrawal = () => {
  const { id } = useParams();
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations]);

  const [applicationData, setApplicationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    application_date: null,
    application_number: "",
    student_name: "",
    reason_for_withdraw: "",
  });
  const [errors, setErrors] = useState({});

  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });

  // Confirmation popup state
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ==================== INITIAL DATA FETCH ====================
  useEffect(() => {
    if (id) {
      fetchAdmissionCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAdmissionCheck = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.post(
        "admission/viewHigherSecondryAdmission",
        { id: id },
      );
      if (response.data?.status) {
        const data = response.data.data;
        setApplicationData(data);
        setFormData({
          application_date: data?.date || null,
          application_number: data?.application_no || "",
          student_name: data?.applicant_name || "",
          reason_for_withdraw: data?.withdraw_reason || "",
        });
      }
    } catch (error) {
      console.error("Admission check error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== HANDLERS ====================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // ==================== VALIDATION ====================
  const validateForm = () => {
    const newErrors = {};
    if (!formData.reason_for_withdraw?.trim()) {
      newErrors.reason_for_withdraw =
        t?.reasonForWithdrawRequired || "Reason for Withdraw is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== STATUS HELPERS ====================
  // API returns values like "Submitted", "Rejected", "Withdrawn", "Approved", etc.
  // Normalize once and compare against lowercase strings.
  const currentStatus = String(applicationData?.status || "").trim();
  const normalizedStatus = currentStatus.toLowerCase();

  // Show withdrawn (read-only) view when already withdrawn
  const isWithdrawn =
    normalizedStatus === "withdrawn" || normalizedStatus === "withdraw";

  // Only "Submitted" status is allowed to withdraw
  const isAllowedToWithdraw = normalizedStatus === "submitted";

  // ==================== SUBMIT FLOW ====================
  // Step 1: form submit -> validate -> check status -> open confirmation popup
  const handleFormSubmit = (e) => {
    e?.preventDefault?.();
    if (!validateForm()) return;

    // Block withdrawal if status is anything other than "Submitted"
    if (!isAllowedToWithdraw) {
      const statusText = currentStatus || "Unknown";
      setModalState({
        open: true,
        type: "Failed",
        message: `Your status is "${statusText}", so you cannot withdraw your application. Please contact the school.`,
        redirectPath: "",
      });
      return;
    }

    setConfirmOpen(true);
  };

  // Step 2: user clicks "Yes, withdraw" in confirmation popup -> call API
  const handleConfirmWithdraw = async () => {
    setConfirmOpen(false);
    try {
      setSubmitting(true);
      const response = await AxiosInstance.post("/admission/withdraw-update", {
        id: id,
        is_secondary: 0,
        reason: formData.reason_for_withdraw,
      });
      if (response.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message || "Application withdrawn successfully",
          redirectPath: "",
        });
        // Refresh data so UI switches to withdrawn view
        fetchAdmissionCheck();
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message: response.data?.message || "Failed to withdraw application",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Withdraw error:", error);
      setModalState({
        open: true,
        type: "Failed",
        message:
          error?.response?.data?.message || "Failed to withdraw application",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
  };

  const handleModalClose = () => {
    setModalState((prev) => ({ ...prev, open: false }));
  };

  const handleSuccessOk = () => {
    setModalState((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      {/* ==================== SCHOOL HEADER BANNER ==================== */}
      <div className={styles.schoolHeader}>
        <div className={styles.schoolLogoWrapper}>
          <img
            src={admissionLogo1}
            alt="School Logo"
            className={styles.schoolLogo}
          />
        </div>
        <div className={styles.schoolHeaderCenter}>
          <h2 className={styles.schoolHeaderTitle}>
            {applicationData?.school_name || t?.schoolName || "School Name"}
          </h2>
          <p className={styles.schoolHeaderSubtitle}>
            {t?.formXItoXII || "Form - XI to XII"}
          </p>
          <p className={styles.schoolHeaderSubtitle}>
            {t?.academicYear || "Academic Year"}:{" "}
            <strong>{applicationData?.academic_years || ""}</strong>
          </p>
        </div>
        <div className={styles.schoolLogoWrapper}>
          <img
            src={admissionLogo2}
            alt="School Logo"
            className={styles.schoolLogo}
          />
        </div>
      </div>

      {/* ==================== APPLICATION DETAILS / WITHDRAWAL FORM ==================== */}
      <div className={styles.pageWrapper}>
        <div className={styles.containerStatus}>
          <div className={styles.header}>
            <span className={styles.headerIcon}>
              <FaIdCard />
            </span>
            <h3 className={styles.headerTitle}>
              {t?.applicationDetails || "Application Details"}
            </h3>
          </div>

          {loading ? (
            <div className={styles.loadingContainer}>
              {t?.loading || "Loading..."}
            </div>
          ) : isWithdrawn ? (
            // ==================== WITHDRAWN VIEW ====================
            <div className="row">
              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomDatePicker
                  label={t?.date || "Date"}
                  name="application_date"
                  value={
                    formData.application_date
                      ? dayjs(formData.application_date)
                      : null
                  }
                  onChange={() => {}}
                  placeholder={t?.dateOfBirthPlaceholder || "Ex: 30/07/2016"}
                  disabled
                />
              </div>

              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={t?.applicationId || "Application Number"}
                  name="application_number"
                  type="text"
                  value={formData.application_number}
                  onChange={() => {}}
                  placeholder={t?.enterHere || "Enter here.."}
                  disabled
                  required
                />
              </div>

              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={t?.studentName || "Student Name"}
                  name="student_name"
                  type="text"
                  value={formData.student_name}
                  onChange={() => {}}
                  placeholder={t?.enterHere || "Enter here.."}
                  disabled
                  required
                />
              </div>

              <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={t?.status || "Status"}
                  name="status"
                  type="text"
                  value={applicationData?.status || ""}
                  onChange={() => {}}
                  disabled
                />
              </div>

              <div className="col-lg-8 px-2 col-md-6 col-sm-12">
                <CustomTextInput
                  label={t?.withdrawReason || "Withdraw Reason"}
                  name="reason_for_withdraw"
                  type="text"
                  value={
                    applicationData?.withdraw_reason ||
                    formData.reason_for_withdraw ||
                    ""
                  }
                  onChange={() => {}}
                  disabled
                />
              </div>
            </div>
          ) : (
            // ==================== WITHDRAWAL FORM ====================
            <form onSubmit={handleFormSubmit}>
              <div className="row">
                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomDatePicker
                    label={t?.date || "Date"}
                    name="application_date"
                    value={
                      formData.application_date
                        ? dayjs(formData.application_date)
                        : null
                    }
                    onChange={() => {}}
                    placeholder={t?.dateOfBirthPlaceholder || "Ex: 30/07/2016"}
                    disabled
                  />
                </div>

                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label={t?.applicationId || "Application Number"}
                    name="application_number"
                    type="text"
                    value={formData.application_number}
                    onChange={handleInputChange}
                    placeholder={t?.enterHere || "Enter here.."}
                    disabled
                    required
                  />
                </div>

                <div className="col-lg-4 px-2 col-md-6 col-sm-12">
                  <CustomTextInput
                    label={t?.studentName || "Student Name"}
                    name="student_name"
                    type="text"
                    value={formData.student_name}
                    onChange={handleInputChange}
                    placeholder={t?.enterHere || "Enter here.."}
                    disabled
                    required
                  />
                </div>

                <div className="col-lg-12 px-2 col-md-12 col-sm-12">
                  <CustomTextInput
                    label={t?.reasonForWithdraw || "Reason For Withdraw"}
                    name="reason_for_withdraw"
                    type="text"
                    value={formData.reason_for_withdraw}
                    onChange={handleInputChange}
                    placeholder={t?.enterHere || "Enter here.."}
                    required
                    error={errors.reason_for_withdraw}
                  />
                </div>
              </div>

              <div className={styles.buttonContainer}>
                <CustomButton
                  type="submit"
                  variant="contained"
                  className={styles.addButton}
                  fullWidth={false}
                  loading={submitting}
                >
                  {t?.withdrawApplication || "Withdraw Application"}
                </CustomButton>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* ==================== CONFIRMATION POPUP ==================== */}
      <PopupModal
        open={confirmOpen}
        type="warning"
        title={t?.confirmWithdrawal || "Confirm Withdrawal"}
        message={
          t?.confirmWithdrawalMessage ||
          "Are you sure you want to withdraw this application?"
        }
        confirmText={t?.yesWithdraw || "Yes, withdraw"}
        cancelText={t?.cancel || "Cancel"}
        onOk={handleConfirmWithdraw}
        onClose={handleCancelConfirm}
        showCancel
      />

      {/* ==================== SUCCESS / FAILED POPUP ==================== */}
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? "Success"
            : modalState.type || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />
    </>
  );
};

export default ApplicationXIIWithdrawal;
